// 🔧 events_list.js
// ✅ Purpose: Super admin-only page to view all active events
// ✅ Features:
// - Loads events from /events
// - Displays in table layout
// - Columns: Event Name, Event Start Date, Event Manager
// - Includes only Edit button
// - No delete, no filter buttons

import React, { useEffect, useState } from "react";
import {
  collection,
  documentId,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { useSearchParams, Link } from "react-router-dom";
import { db } from "../../firebase";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from '../../utils/logger';
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  LGV_REVENUE_ADDONS,
} from "../../constants/lgv_features";

// =====================================================
// 👥 Load only event manager profiles
// =====================================================
// This replaces useUsersMap(), which loads every global user.
// Firestore "in" queries support limited batches, so owner IDs are chunked.

const loadUserProfilesByIds = async (userIds = []) => {
  const cleanIds = [...new Set(userIds.filter(Boolean))];

  if (!cleanIds.length) return {};

  const chunks = [];

  for (let i = 0; i < cleanIds.length; i += 10) {
    chunks.push(cleanIds.slice(i, i + 10));
  }

  const map = {};

  for (const chunk of chunks) {
    const usersQuery = query(
      collection(db, "users"),
      where(documentId(), "in", chunk)
    );

    const snap = await getDocs(usersQuery);

    snap.forEach((docSnap) => {
      const d = docSnap.data();

      map[docSnap.id] = {
        firstName: d.firstName || "",
        lastName: d.lastName || "",
        email: d.email || "",
        phone: d.phone || "",
        label:
          `${d.firstName || ""} ${d.lastName || ""}`.trim() ||
          d.email ||
          "Unknown",
      };
    });
  }

  return map;
};

const AdminEventsList = () => {

  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersMap, setUsersMap] = useState({});
  const [usersMapLoading, setUsersMapLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get("status");
  const [status, setStatus] = useState(urlStatus || "");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 50;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (!status) return;

    const timer = setTimeout(() => {
      setStatus("");
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);
  
  const loadEvents = async () => {
    setLoading(true);
    setUsersMapLoading(true);

    try {
      const snap = await getDocs(collection(db, "events"));

      const eventList = snap.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .sort((a, b) => {
          const getCreatedTime = (eventRecord) => {
            const rawDate =
              eventRecord?.createdAt ||
              eventRecord?.lgvPackage?.grantedAt ||
              eventRecord?.startDate ||
              0;

            if (rawDate?.toDate) {
              return rawDate.toDate().getTime();
            }

            return new Date(rawDate || 0).getTime();
          };

          return getCreatedTime(b) - getCreatedTime(a);
        });

      const ownerIds = eventList
        .map((eventRecord) => eventRecord.ownerId)
        .filter(Boolean);

      const managerMap = await loadUserProfilesByIds(ownerIds);

      setEvents(eventList);
      setUsersMap(managerMap);
    } catch (err) {
      logger.error("❌ Failed to load events:", {
        value: err,
      });

      setStatus("❌ Failed to load events.");
    } finally {
      setLoading(false);
      setUsersMapLoading(false);
    }
  };

  useEffect(() => {
      loadEvents();
    }, []);

    useEffect(() => {
    if (!status) return;

    const timer = setTimeout(() => {
      setStatus("");
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  // ✅ Deletes an event and known nested event data
  // ✅ IMPORTANT: Firestore does NOT automatically delete subcollections
  // ✅ This manually deletes known Let'sGoVote event subcollections before deleting the event doc
  const handleDeleteEvent = async (eventRecord) => {
    const eventName = eventRecord?.name || "Untitled";

    const typedName = window.prompt(
      `Type the event name exactly to delete this event:\n\n${eventName}`
    );

    if (typedName !== eventName) {
      alert("Delete canceled. Event name did not match.");
      return;
    }

    const finalConfirm = window.confirm(
      `This will permanently delete "${eventName}" and all nested event data. Continue?`
    );

    if (!finalConfirm) return;

    try {
      setProcessing(true);
      setProcessingMessage(`Deleting ${eventName}...`);

      const functions = getFunctions();
      const deleteEventCascade = httpsCallable(functions, "deleteEventCascade");

      const result = await deleteEventCascade({
        eventId: eventRecord.id,
      });

      logger.log("✅ Event deleted by Cloud Function", {
        value: result.data,
      });

      setStatus(`✅ Event deleted: ${eventName}`);
      await loadEvents();
    } catch (err) {
      logger.error("❌ Failed to delete event:", {
        value: err,
      });

      alert(err?.message || "Failed to delete event.");
    } finally {
      setProcessing(false);
      setProcessingMessage("");
    }
  };

  const formatDate = (val) => {
    if (!val) return "—";
    try {
      const date = val.toDate ? val.toDate() : new Date(val);
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (err) {
      logger.warn("⚠️ Failed to format date:", val, err);
      return "Invalid Date";
    }
  };

  // =====================================================
  // 💰 Get purchased add-on names
  // =====================================================
  // Purpose:
  // - Converts purchasedAddOns object into display badges
  // - Used on Admin Events cards
  // =====================================================
  const getPurchasedAddOns = (eventRecord) => {
    const purchasedAddOns =
      eventRecord?.purchasedAddOns || {};

    return LGV_REVENUE_ADDONS.filter(
      (addOn) =>
        purchasedAddOns?.[addOn.key] === true
    );
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredEvents = events.filter((eventRecord) => {
    const manager = usersMap?.[eventRecord.ownerId] || {};

    return [
      eventRecord.name,
      eventRecord.slug,
      eventRecord.votingType,
      manager.firstName,
      manager.lastName,
      manager.email,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(normalizedSearch);
  });

  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);

  const paginatedEvents = filteredEvents.slice(
    (currentPage - 1) * eventsPerPage,
    currentPage * eventsPerPage
  );

  if (loading || usersMapLoading) {
    return (
      <FullPageSpinner
        message="Loading All Events..."
      />
    );
  }

  if (processing) {
    return (
      <FullPageSpinner
        message={
          processingMessage ||
          "Processing..."
        }
      />
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1400px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">🛠 Admin Events</h1>
            <p className="lead text-muted mb-0">
              Manage all Let's Go Vote events, demo events, approvals, and administration.
            </p>
          </div>

        </div>

        {(status || urlStatus) && (
          <div className="alert alert-success mb-4">
            {status || urlStatus}
          </div>
        )}

        <div className="row g-4 mb-4">
          <div className="col-12 col-md-6">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Total Events</div>
                <div className="fs-3 fw-bold">{events.length}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <Link
                  to="/pending_events_list"
                  className="text-decoration-none text-dark"
                >
                  <div className="card h-100 shadow-sm">
                    <div className="card-body">
                      <div className="text-muted small"></div>
                      <div className="fs-3 fw-bold"></div>
                      <div className="small text-muted"></div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>

        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Event Administration Tools</strong>
          </div>

          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-lg-7">
                <label className="form-label">Search Events</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by event, slug, voting type, manager, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="col-12 col-lg-5">
                <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                  {searchTerm && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setSearchTerm("")}
                    >
                      Reset Search
                    </button>
                  )}

                  <Link
                    to="/event"
                    className="btn btn-success"
                  >
                    ➕ Create New Event
                  </Link>

                  <Link
                    to="/demo_builder"
                    className="btn btn-outline-secondary"
                  >
                    🧪 Demo Builder
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {filteredEvents.length === 0 ? (
          <div className="card shadow-sm">
            <div className="card-body text-center py-5">
              <h4>No Events Found</h4>
              <p className="text-muted mb-0">
                No events match the current search.
              </p>
            </div>
          </div>
        ) : (
          <>
          
            <div className="card shadow-sm">
              <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
                <div>
                  <strong>Events</strong>
                  <div className="small text-muted">
                    Showing {paginatedEvents.length} of {filteredEvents.length} events.
                    Sorted by created date, newest first.
                  </div>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Created</th>
                      <th>Event</th>
                      <th>Start</th>
                      <th>Manager</th>
                      <th>Add-ons</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedEvents.map((eventRecord) => {
                      const manager = usersMap?.[eventRecord.ownerId];

                      const purchasedAddOns = getPurchasedAddOns(eventRecord);

                      return (
                        <tr key={eventRecord.id}>
                          <td className="text-nowrap">
                            {formatDate(
                              eventRecord.createdAt ||
                                eventRecord?.lgvPackage?.grantedAt ||
                                eventRecord.startDate
                            )}
                          </td>

                          <td style={{ minWidth: "260px" }}>
                            <div className="fw-bold">
                              <Link
                                to={`/${eventRecord.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {eventRecord.name || "Untitled Event"}
                              </Link>

                            </div>

                            <div className="small text-muted text-break">
                              /{eventRecord.slug || "missing-slug"}
                            </div>
                          </td>

                          <td className="text-nowrap">
                            {formatDate(eventRecord.startDate)}
                          </td>

                          <td style={{ minWidth: "220px" }}>
                            {manager ? (
                              <>
                                <div className="fw-semibold">
                                  {`${manager.firstName || ""} ${manager.lastName || ""}`.trim() ||
                                    "Unknown"}
                                </div>

                                <div className="small text-muted text-break">
                                  {manager.email || "no email"}
                                </div>
                              </>
                            ) : (
                              <span className="text-muted">Unknown</span>
                            )}
                          </td>

                          <td style={{ minWidth: "180px" }}>
                            {purchasedAddOns.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {purchasedAddOns.map((addOn) => (
                                  <span key={addOn.key} className="badge bg-success">
                                    {addOn.icon} {addOn.name}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="small text-muted">None</span>
                            )}
                          </td>

                          <td className="text-end text-nowrap">
                            <div className="btn-group btn-group-sm">
                              <Link
                                to={`/event/${eventRecord.slug || eventRecord.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-secondary"
                              >
                                Edit
                              </Link>

                              <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={() => handleDeleteEvent(eventRecord)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="card shadow-sm mt-4">
                <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <button
                    className="btn btn-outline-secondary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  >
                    ⬅ Prev
                  </button>

                  <div className="d-flex align-items-center gap-2">
                    <span>Page</span>

                    <select
                      className="form-select"
                      style={{ width: "auto" }}
                      value={currentPage}
                      onChange={(e) => setCurrentPage(Number(e.target.value))}
                    >
                      {Array.from({ length: totalPages }, (_, index) => index + 1).map(
                        (pageNumber) => (
                          <option key={pageNumber} value={pageNumber}>
                            {pageNumber}
                          </option>
                        )
                      )}
                    </select>

                    <span>of {totalPages}</span>
                  </div>

                  <button
                    className="btn btn-outline-secondary"
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                  >
                    Next ➡
                  </button>
                </div>
              </div>
            )}
          </>
        )}

      </div>
    </section>
  );

};

export default AdminEventsList;
